// ExternalAppHost — standalone runtime for an "external" builder app.
//
// Lives at /x/:appId. Two-step boot:
//
//   1. Ask the backend whether this app has a successful published build.
//      We hit GET ${hostname}/apps/<id>/published (anonymous, returns just
//      a `published` flag + build id). If yes, hand off via a full
//      navigation to ${hostname}/x/<appId> — Flask streams the published
//      index.html from Spaces, and the bundle's internal asset paths
//      (/x/<appId>/<buildId>/...) resolve via `serve_published_asset` on
//      the same host. This keeps the published bundle authoritative even
//      when the builder has unsaved changes.
//
//   2. If no published build exists, fall through to the live in-browser
//      runtime (AppRuntime mode='external'), which compiles the current
//      files in a sandboxed iframe.
//
// The probe is required because Netlify's `_redirects` sends /x/* to the
// SPA's index.html — so without this check we'd always render the live
// runtime even when a published bundle exists.

import React from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Alert, Box, CircularProgress } from "@mui/material";

import { hostname } from "../utils";
import AppRuntime from "../dashboard/app-builder/runtime/AppRuntime";

type ProbeState =
  | { phase: "loading" }
  | { phase: "live" }
  | { phase: "error"; message: string };

const ExternalAppHost: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const [state, setState] = React.useState<ProbeState>({ phase: "loading" });

  React.useEffect(() => {
    if (!appId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${hostname}/apps/${appId}/published`);
        if (cancelled) return;
        if (r.data?.published) {
          // Hand off to the published bundle on the Flask host. Use
          // replace() so the back button doesn't bounce the user back
          // here into a redirect loop.
          window.location.replace(`${hostname}/x/${appId}`);
          return;
        }
        setState({ phase: "live" });
      } catch (e: any) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setState({ phase: "error", message: "App not found." });
          return;
        }
        // Any other failure: fall through to the live runtime rather than
        // hard-failing. The live runtime has its own error surfaces.
        setState({ phase: "live" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appId]);

  if (!appId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Missing app id in URL.</Alert>
      </Box>
    );
  }

  if (state.phase === "loading") {
    return (
      <Box sx={{ width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (state.phase === "error") {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{state.message}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100vw", height: "100vh" }}>
      <AppRuntime mode="external" appId={appId} hostname={hostname} />
    </Box>
  );
};

export default ExternalAppHost;
